import React, { useState, useEffect } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useUserRole } from "../../../../context/UserRoleContext";
import EditBarModal from "./EditBarModal";
import ConfirmModal from "../../../../components/common/ConfirmModal";
import useBars from "../../hooks/useBars";
import { getWeeklyHoursDisplayLines } from "../../../../utils/barWeeklyHours";
import {
  parseTimeSlots,
  DAY_ORDER,
  WEEKDAY_LABEL_SHORT,
} from "../../../../../lib/barTimeSlots";
import defaultBarIndoor from "../../../../assets/images/default-bar-indoor.png";
import defaultBarOutdoor from "../../../../assets/images/default-bar-outdoor.png";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551,
  lng: -81.78,
};

const BarDetailModal = ({
  isOpen,
  onClose,
  bar,
  isMapsLoaded = false,
  onBarUpdated,
  onBarDeleted,
  onBarsRefresh,
}) => {
  const { userData } = useUserRole();
  const { deleteBar } = useBars();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);

  if (!isOpen || !bar) return null;

  const isOwner = bar.owner === userData?.user_id;

  const parseBarField = (val, defaultVal) => {
    if (val == null) return defaultVal;
    if (typeof val === "string") {
      try {
        return JSON.parse(val) || defaultVal;
      } catch {
        return defaultVal;
      }
    }
    return val;
  };

  const indoorPhotosData = parseBarField(bar.indoor_photos, { photos: [] });
  const outdoorPhotosData = parseBarField(bar.outdoor_photos, { photos: [] });
  const tsParsed = parseTimeSlots(bar.time_slots);

  const getIndoorPhotos = () => {
    if (indoorPhotosData?.photos?.length > 0) return indoorPhotosData.photos;
    return [defaultBarIndoor];
  };

  const getOutdoorPhotos = () => {
    if (outdoorPhotosData?.photos?.length > 0) return outdoorPhotosData.photos;
    return [defaultBarOutdoor];
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    try {
      const [hours, minutes] = String(time).split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes || "00"} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const startTimeToMinutes = (time) => {
    if (!time) return 24 * 60;
    const [hours, minutes] = String(time).split(":");
    const h = parseInt(hours, 10);
    const m = parseInt(minutes || "0", 10);
    if (isNaN(h) || isNaN(m)) return 24 * 60;
    return h * 60 + m;
  };

  const sortSlots = (list) =>
    [...(list || [])].sort(
      (a, b) =>
        startTimeToMinutes(a.start_time) - startTimeToMinutes(b.start_time)
    );

  const defaultSlotsSorted = sortSlots(tsParsed.defaultSlots);

  useEffect(() => {
    if (isOpen && bar) {
      const lat = bar.latitude != null ? parseFloat(bar.latitude) : null;
      const lng = bar.longitude != null ? parseFloat(bar.longitude) : null;
      if (
        lat != null &&
        lng != null &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        (lat !== 0 || lng !== 0)
      ) {
        const center = { lat, lng };
        setMapCenter(center);
        setMarkerPosition(center);
        setMapZoom(14);
      } else {
        setMapCenter(defaultCenter);
        setMarkerPosition(null);
        setMapZoom(13);
      }
    }
  }, [isOpen, bar]);

  useEffect(() => {
    if (!isOpen) {
      setMapExpanded(false);
      setMapCenter(defaultCenter);
      setMarkerPosition(null);
      setMapZoom(13);
    }
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleEditSuccess = (updatedBar) => {
    setShowEditModal(false);
    if (onBarUpdated) onBarUpdated(updatedBar);
    if (onBarsRefresh) onBarsRefresh();
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleDeleteCancel = () => setShowDeleteConfirm(false);

  const handleDeleteConfirm = async () => {
    if (!bar?.bar_id) return;
    setIsDeleting(true);
    try {
      const result = await deleteBar(bar.bar_id);
      if (result.success) {
        setShowDeleteConfirm(false);
        if (onBarDeleted) onBarDeleted(bar.bar_id);
        if (onBarsRefresh) onBarsRefresh();
        onClose();
      } else {
        alert(result.error || "Failed to delete bar.");
      }
    } catch (err) {
      console.error("Delete bar error:", err);
      alert("Failed to delete bar. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="bar-modal-overlay" onClick={handleOverlayClick}>
        <div
          className="bar-modal bar-detail-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bar-modal-header">
            <div className="bar-modal-header-content">
              <h2>{bar.name || "Bar"}</h2>
              {bar.address && (
                <p className="bar-modal-address">📍 {bar.address}</p>
              )}
            </div>
            <div className="artist-profile-modal-header-actions">
              <button
                type="button"
                className="artist-profile-invite-btn"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </button>
              {isOwner && (
                <button
                  type="button"
                  className="bar-modal-leave-btn danger-btn"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  aria-label="Delete bar"
                >
                  Delete
                </button>
              )}
              <button
                className="bar-modal-close"
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bar-modal-body">
            {/* Hours Section */}
            {getWeeklyHoursDisplayLines(bar, formatTime).length > 0 && (
              <div className="onboarding-section">
                <h2 className="section-title">Hours</h2>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "0.5rem 0 0",
                    lineHeight: 1.5,
                  }}
                >
                  {getWeeklyHoursDisplayLines(bar, formatTime).map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Location Section */}
            {bar.latitude != null && bar.longitude != null && (
              <div className="onboarding-section">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: mapExpanded ? "1rem" : "0",
                  }}
                >
                  <h2 className="section-title">Location</h2>
                  <button
                    type="button"
                    onClick={() => setMapExpanded(!mapExpanded)}
                    className="bar-modal-map-toggle"
                    aria-label={mapExpanded ? "Collapse map" : "Expand map"}
                  >
                    {mapExpanded ? "Hide Map" : "Show Map"}
                  </button>
                </div>
                {mapExpanded && (
                  <div
                    className="input-group full-width"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isMapsLoaded ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={mapCenter}
                          zoom={mapZoom}
                          options={{ gestureHandling: "cooperative" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {markerPosition && (
                            <Marker
                              position={markerPosition}
                              draggable={false}
                            />
                          )}
                        </GoogleMap>
                      </div>
                    ) : (
                      <div
                        style={{
                          ...mapContainerStyle,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                          color: "#666",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Loading map...
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Outdoor Photos */}
            {getOutdoorPhotos().length > 0 && (
              <div className="onboarding-section">
                <h2 className="section-title">Outdoor Photos</h2>
                <div className="bar-modal-photos">
                  {getOutdoorPhotos().map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`${bar.name} outdoor ${index + 1}`}
                      className="bar-modal-photo"
                      onError={(e) => {
                        e.currentTarget.src = defaultBarOutdoor;
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Indoor Photos */}
            <div className="onboarding-section">
              <h2 className="section-title">Indoor Photos</h2>
              <div className="bar-modal-photos">
                {getIndoorPhotos().map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`${bar.name} indoor ${index + 1}`}
                    className="bar-modal-photo"
                    onError={(e) => {
                      e.currentTarget.src = defaultBarIndoor;
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Show Time Slots */}
            <div className="onboarding-section">
              <h2 className="section-title">Show Time Slots</h2>
              {!tsParsed.enabled ? (
                <p style={{ margin: "0.5rem 0 0", lineHeight: 1.5 }}>
                  Named show times are off. Schedule shows with custom start and
                  end times only.
                </p>
              ) : (
                <>
                  <p
                    style={{
                      margin: "0.25rem 0 0.5rem",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    Default (days without an override)
                  </p>
                  {defaultSlotsSorted.length === 0 ? (
                    <p style={{ margin: "0 0 0.75rem" }}>None — custom times.</p>
                  ) : (
                    <ul className="bar-detail-time-slots-list">
                      {defaultSlotsSorted.map((slot, i) => (
                        <li
                          key={slot.id || i}
                          className="bar-detail-time-slot-item"
                        >
                          <span className="bar-detail-slot-name">
                            {slot.name}
                          </span>
                          <span className="bar-detail-slot-times">
                            {slot.start_time
                              ? formatTime(slot.start_time)
                              : "—"}{" "}
                            –{" "}
                            {slot.end_time ? formatTime(slot.end_time) : "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {DAY_ORDER.map((d) => {
                    if (
                      !tsParsed.byDay ||
                      !Object.prototype.hasOwnProperty.call(
                        tsParsed.byDay,
                        d
                      )
                    ) {
                      return null;
                    }
                    const v = tsParsed.byDay[d];
                    if (v === null || v === undefined) return null;
                    const label = WEEKDAY_LABEL_SHORT[d] || d;
                    return (
                      <div key={d} style={{ marginTop: "1rem" }}>
                        <p
                          style={{
                            margin: "0 0 0.5rem",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                          }}
                        >
                          {label}
                        </p>
                        {Array.isArray(v) && v.length === 0 ? (
                          <p style={{ margin: 0 }}>No named slots.</p>
                        ) : (
                          <ul className="bar-detail-time-slots-list">
                            {sortSlots(v).map((slot, i) => (
                              <li
                                key={`${d}-${slot.id || i}`}
                                className="bar-detail-time-slot-item"
                              >
                                <span className="bar-detail-slot-name">
                                  {slot.name}
                                </span>
                                <span className="bar-detail-slot-times">
                                  {slot.start_time
                                    ? formatTime(slot.start_time)
                                    : "—"}{" "}
                                  –{" "}
                                  {slot.end_time
                                    ? formatTime(slot.end_time)
                                    : "—"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bar-modal-footer">
            <button
              type="button"
              className="bar-modal-close-btn"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <EditBarModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        bar={bar}
        onSuccess={handleEditSuccess}
        isMapsLoaded={isMapsLoaded}
        overlayZIndex={1200}
      />

      <ConfirmModal
        open={showDeleteConfirm}
        title="Delete Bar"
        message="This will remove the bar from your list. Future shows will be cancelled and artists will be notified. Past shows and earnings for artists will remain. Bar admins will be notified. This cannot be undone."
        confirmLabel="Yes, Delete Bar"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isConfirming={isDeleting}
      />
    </>
  );
};

export default BarDetailModal;
